import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  AutoCompleteSelectServiceUI,
  SelectUI,
} from '../Interface';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';
import { RelatedItemTypeList } from '../../config/item';

const EditToolbar = (props) => {
  const { disabled, push, defRelatedItem } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defRelatedItem,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `relatedItem.${values.relatedItem.length ?? 0}.relationType`,
      `relatedItem.${values.relatedItem.length ?? 0}.relatedItemCode`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const RelatedItemTable = ({ disabled, defRelatedItem }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.relatedItem.findIndex((rc) => rc.id === id);
  };

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'relationType',
        headerName: 'Típus',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedType =
            RelatedItemTypeList.find((type) => type.value === values.relatedItem?.[valueIndex]?.relationType)?.label ??
            '';
          return findedType;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`relatedItem.${valueIndex}.relationType`}
              type="text"
              variant="standard"
              component={SelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.relatedItem?.[valueIndex]?.relationType} />}
              option={RelatedItemTypeList}
              onChange={(e) => {
                setFieldValue(`relatedItem.${valueIndex}.relationType`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              value={values.relatedItem?.[valueIndex]?.relationType ?? ''}
              onBlur={async () => await validateField(`relatedItem.${valueIndex}.relationType`)}
            />
          );
        },
      },
      {
        field: 'relatedItemCode',
        headerName: 'Kapcsolódó termék',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.relatedItem?.[valueIndex]?.relatedItemCode ?? '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const rowRelatedItemCode =
            data.value ?? data.row?.relatedItemCode ?? values.relatedItem?.[valueIndex]?.relatedItemCode ?? null;
          return (
            <Field
              name={`relatedItem.${valueIndex}.relatedItemCode`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectServiceUI}
              helperText={<FormErrorUI message={errors?.relatedItem?.[valueIndex]?.relatedItemCode} />}
              selectedValue={rowRelatedItemCode}
              selectedLabelValue={rowRelatedItemCode ?? ''}
              service={itemAutocompleteService}
              serviceRequestBuilder={itemAutocompleteRequestBuilder}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;
                setFieldValue(`relatedItem.${valueIndex}.relatedItemCode`, val);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`relatedItem.${valueIndex}.relatedItemCode`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="relatedItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.RelatedItemTable}
                height={480}
                columns={columns(remove)}
                rows={values?.relatedItem ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defRelatedItem,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default RelatedItemTable;
